import { useEffect, useMemo, useState } from "react";
import {
  quickInteractionCaptureSchema,
  type QuickInteractionCapture as QuickInteractionCaptureDto,
} from "../../../../shared/src/domain/quick-interaction.js";
import type {
  Contact,
  DuplicateCandidate,
  Lab,
  Location,
  Relationship,
  SavedInteraction,
} from "../../api/types.js";
import { ApiError, type ApiClient } from "../../api/client.js";
import {
  formatDate,
  formatDateTimeLocal,
  localDateTimeToISOString,
  createClientRequestId,
} from "../../app/presentation.js";

type TimingPrecision =
  | "exact_date"
  | "month"
  | "date_range"
  | "season"
  | "relative"
  | "unknown";

interface CategoryDraft {
  enabled: boolean;
  quantity: string;
  quantityScope: "current_installed_base" | "historical_service";
  qualifier: "approximate" | "exact";
  effectiveDate: string;
  timingPrecision: TimingPrecision;
  exactDate: string;
  year: string;
  month: string;
  rangeStart: string;
  rangeEnd: string;
  season: "spring" | "summer" | "fall" | "winter";
  relativeState: "due_soon" | "overdue" | "just_serviced";
  referenceDate: string;
  cycleMonths: string;
  provider: string;
  accreditationRequirement: "no" | "unknown" | "yes";
}

type QuickOutcome =
  | "nobody_available"
  | "wrong_person"
  | "no_new_information"
  | "referred_elsewhere";

interface QuickInteractionCaptureProps {
  api: ApiClient;
  labId?: string;
  labName?: string;
  location: Location;
  contacts: Contact[];
  relationships?: Relationship[];
  /** Labs already known at a Location, used only when service facts are added. */
  availableLabs?: Lab[];
  /** Lets a Location capture hand off to the existing New Lab flow. */
  onRequestNewLab?: () => void;
  /**
   * Which contact the capture should start on. A desktop workbench row already
   * knows the Lab's confirmed calibration contact, so the drawer must not ask
   * the user to reselect context the CRM already holds.
   */
  initialContactId?: string | undefined;
  /**
   * `sheet` is the phone-centred field overlay. `drawer` is the desktop office
   * presentation: a side panel that leaves the working set visible behind it.
   * Both render the same workflow; this is presentation only.
   */
  presentation?: "sheet" | "drawer";
  onClose: () => void;
  onSaved: (result: SavedInteraction, summary: string) => Promise<void> | void;
}

const monthLabels = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const seasonLabels = {
  spring: "Spring",
  summer: "Summer",
  fall: "Fall",
  winter: "Winter",
} as const;

const quickOutcomeLabels: Record<QuickOutcome, string> = {
  nobody_available: "Nobody available",
  wrong_person: "Wrong person",
  no_new_information: "No useful information",
  referred_elsewhere: "Referred elsewhere",
};

const lightweightOutcomes = new Set<QuickOutcome>([
  "nobody_available",
  "wrong_person",
  "no_new_information",
  "referred_elsewhere",
]);

const relativeTimingLabels: Record<string, string> = {
  due_soon: "Due soon",
  overdue: "Overdue",
  just_serviced: "Just serviced",
};

const initialCategory = (enabled = false): CategoryDraft => ({
  enabled,
  quantity: "",
  quantityScope: "current_installed_base",
  qualifier: "approximate",
  effectiveDate: "",
  timingPrecision: "unknown",
  exactDate: "",
  year: "",
  month: "",
  rangeStart: "",
  rangeEnd: "",
  season: "fall",
  relativeState: "due_soon",
  referenceDate: "",
  cycleMonths: "",
  provider: "",
  accreditationRequirement: "unknown",
});

function categoryHasFacts(category: CategoryDraft): boolean {
  return Boolean(
    category.quantity.trim() ||
      category.provider.trim() ||
      category.accreditationRequirement !== "unknown" ||
      category.timingPrecision !== "unknown",
  );
}

function buildTiming(
  category: CategoryDraft,
  interactionType: string,
): Record<string, unknown> | undefined {
  if (category.timingPrecision === "unknown") return undefined;
  const metadata =
    interactionType === "sticker_observation"
      ? { source: "sticker_observed", confidence: "high" }
      : {};
  const timing: Record<string, unknown> = {
    precision: category.timingPrecision,
    ...metadata,
  };
  switch (category.timingPrecision) {
    case "exact_date":
      if (category.exactDate) timing.exactDate = category.exactDate;
      break;
    case "month":
      if (category.month) timing.month = Number(category.month);
      if (category.year) timing.year = Number(category.year);
      break;
    case "date_range":
      if (category.rangeStart) timing.rangeStart = category.rangeStart;
      if (category.rangeEnd) timing.rangeEnd = category.rangeEnd;
      break;
    case "season":
      timing.season = category.season;
      if (category.year) timing.year = Number(category.year);
      break;
    case "relative":
      timing.relativeState = category.relativeState;
      if (category.referenceDate) timing.referenceDate = category.referenceDate;
      break;
    default:
      return undefined;
  }
  if (category.cycleMonths) timing.cycleMonths = Number(category.cycleMonths);
  return timing;
}

function buildCategory(
  category: CategoryDraft,
  interactionType: string,
): Record<string, unknown> | undefined {
  if (!categoryHasFacts(category)) return undefined;
  const metadata =
    interactionType === "sticker_observation"
      ? { source: "sticker_observed", confidence: "high" }
      : {};
  const value: Record<string, unknown> = {};
  if (category.quantity.trim()) {
    const quantity: Record<string, unknown> = {
      scope: category.quantityScope,
      value: Number(category.quantity),
      qualifier: category.qualifier,
      ...metadata,
    };
    if (category.effectiveDate) quantity.effectiveDate = category.effectiveDate;
    value.quantity = quantity;
  }
  const timing = buildTiming(category, interactionType);
  if (timing) value.timing = timing;
  if (category.provider.trim()) {
    value.provider = { name: category.provider.trim(), ...metadata };
  }
  if (category.accreditationRequirement !== "unknown") {
    value.accreditationRequirement = {
      requirement: category.accreditationRequirement,
      ...metadata,
    };
  }
  return value;
}

function contactLabel(contact: Contact): string {
  return [contact.name, contact.title].filter(Boolean).join(" · ");
}

function timingLabel(timing: {
  precision: string;
  month?: number | undefined;
  year?: number | undefined;
  season?: string | undefined;
  exactDate?: string | undefined;
  rangeStart?: string | undefined;
  rangeEnd?: string | undefined;
  relativeState?: string | undefined;
}): string | null {
  switch (timing.precision) {
    case "month":
      return `${monthLabels[(timing.month ?? 0) - 1] ?? "Month"}${timing.year ? ` ${timing.year}` : ""}`;
    case "season":
      return `${seasonLabels[timing.season as keyof typeof seasonLabels] ?? "Season"}${timing.year ? ` ${timing.year}` : ""}`;
    case "exact_date":
      return timing.exactDate ?? "Exact date";
    case "date_range":
      return timing.rangeStart && timing.rangeEnd
        ? `${timing.rangeStart} – ${timing.rangeEnd}`
        : "Date range";
    case "relative":
      return (
        relativeTimingLabels[timing.relativeState ?? ""] ?? "Relative timing"
      );
    default:
      return null;
  }
}

function savedSummary(
  capture: QuickInteractionCaptureDto,
  result: SavedInteraction,
): string {
  const categories: Array<{
    key: "pipettes" | "balances";
    label: string;
    category: QuickInteractionCaptureDto["pipettes"];
  }> = [
    { key: "pipettes", label: "Pipettes", category: capture.pipettes },
    { key: "balances", label: "Balances", category: capture.balances },
  ];
  const lines = categories.flatMap(({ category, label }) => {
    if (!category) return [];
    const facts: string[] = [];
    if (category.quantity) {
      facts.push(
        `${category.quantity.qualifier === "approximate" ? "~" : ""}${category.quantity.value}`,
      );
    }
    if (category.timing) {
      const value = timingLabel(category.timing);
      if (value) facts.push(value);
    }
    if (category.provider) facts.push(category.provider.name);
    if (facts.length === 0) return [];
    return [`${label}\n${facts.join(" · ")}`];
  });
  if (lines.length === 0) {
    return result.idempotent
      ? "Already saved."
      : `Saved — ${capture.captureOutcome === "nobody_available" ? "Nobody available" : capture.captureOutcome === "wrong_person" ? "Wrong person" : capture.captureOutcome === "no_new_information" ? "No useful information" : capture.captureOutcome === "referred_elsewhere" ? "Referred elsewhere" : "Interaction recorded"}.`;
  }
  const nextDate = result.updatedProfiles.find(
    (item) => item.nextMeaningfulSalesDate,
  )?.nextMeaningfulSalesDate;
  return [
    result.idempotent ? "Already saved." : "Saved",
    ...lines,
    ...(nextDate ? [`Next attention: ${formatDate(nextDate)}`] : []),
  ].join("\n");
}

export function QuickInteractionCapture({
  api,
  labId,
  labName,
  location,
  contacts,
  relationships = [],
  availableLabs = [],
  onRequestNewLab,
  initialContactId,
  presentation = "sheet",
  onClose,
  onSaved,
}: QuickInteractionCaptureProps) {
  const clientRequestId = useMemo(createClientRequestId, []);
  const [interactionType, setInteractionType] = useState("field_visit");
  const [communicationDirection, setCommunicationDirection] =
    useState("unknown");
  const [captureOutcome, setCaptureOutcome] = useState<QuickOutcome | null>(
    null,
  );
  const [occurredAt, setOccurredAt] = useState(() =>
    formatDateTimeLocal(new Date()),
  );
  const [personQuery, setPersonQuery] = useState(
    () =>
      contacts.find((contact) => contact.id === initialContactId)?.name ?? "",
  );
  const [contactId, setContactId] = useState(initialContactId ?? "");
  const [confirmIdentity, setConfirmIdentity] = useState(false);
  const [recordResponsibility, setRecordResponsibility] = useState(false);
  const [responsibilityRole, setResponsibilityRole] =
    useState<Relationship["relationshipRole"]>(null);
  const [calibrationResponsible, setCalibrationResponsible] = useState<
    boolean | null
  >(null);
  const [influenceScope, setInfluenceScope] =
    useState<Relationship["influenceScope"]>(null);
  const [relationshipStatus, setRelationshipStatus] =
    useState<Relationship["relationshipStatus"]>("confirmed");
  const [scopeNote, setScopeNote] = useState("");
  const [pipettes, setPipettes] = useState<CategoryDraft>(() =>
    initialCategory(Boolean(labId)),
  );
  const [balances, setBalances] = useState<CategoryDraft>(() =>
    initialCategory(),
  );
  const [note, setNote] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [selectedLabId, setSelectedLabId] = useState(labId ?? "");
  const [serviceDetailsOpen, setServiceDetailsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [duplicateCandidates, setDuplicateCandidates] = useState<
    DuplicateCandidate[]
  >([]);

  const activeLabId = (labId ?? selectedLabId) || undefined;
  const activeLab = availableLabs.find((lab) => lab.id === activeLabId);
  const activeLabName = labId ? labName : activeLab?.name;
  const selectedContact = contacts.find((contact) => contact.id === contactId);
  const relationshipForContact = relationships.find(
    (relationship) => relationship.contact.id === contactId,
  );
  useEffect(() => {
    if (relationshipForContact === undefined) {
      setResponsibilityRole(null);
      setCalibrationResponsible(null);
      setInfluenceScope(null);
      setRelationshipStatus("confirmed");
      setScopeNote("");
      return;
    }
    setResponsibilityRole(relationshipForContact.relationshipRole);
    setCalibrationResponsible(relationshipForContact.calibrationResponsible);
    setInfluenceScope(relationshipForContact.influenceScope);
    setRelationshipStatus(relationshipForContact.relationshipStatus);
    setScopeNote(relationshipForContact.scopeNote);
  }, [
    contactId,
    relationshipForContact?.relationshipRole,
    relationshipForContact?.calibrationResponsible,
    relationshipForContact?.influenceScope,
    relationshipForContact?.relationshipStatus,
    relationshipForContact?.scopeNote,
  ]);
  const lightweightCapture =
    captureOutcome !== null &&
    lightweightOutcomes.has(captureOutcome) &&
    interactionType !== "sticker_observation" &&
    !serviceDetailsOpen;
  const stickerCapture = interactionType === "sticker_observation";
  const serviceFactsVisible =
    activeLabId !== undefined &&
    (stickerCapture || captureOutcome === null || serviceDetailsOpen);

  function updatePerson(value: string): void {
    const match = contacts.find(
      (contact) =>
        contact.isActive &&
        contact.name.trim().toLocaleLowerCase() ===
          value.trim().toLocaleLowerCase(),
    );
    setPersonQuery(value);
    setContactId(match?.id ?? "");
  }

  function chooseInteractionType(value: string): void {
    setInteractionType(value);
    if (value === "sticker_observation") {
      setCaptureOutcome(null);
      setServiceDetailsOpen(true);
      setPipettes((current) => ({ ...current, enabled: true }));
      setPersonQuery("");
      setContactId("");
    } else if (captureOutcome === null && activeLabId) {
      setPipettes((current) => ({ ...current, enabled: true }));
    }
  }

  function selectQuickOutcome(value: QuickOutcome): void {
    setCaptureOutcome(value);
    setServiceDetailsOpen(false);
  }

  function selectLab(value: string): void {
    setSelectedLabId(value);
    if (value) {
      setServiceDetailsOpen(true);
      setPipettes((current) => ({ ...current, enabled: true }));
    }
  }

  function updateCategory(
    category: "pipettes" | "balances",
    changes: Partial<CategoryDraft>,
  ): void {
    const setter = category === "pipettes" ? setPipettes : setBalances;
    setter((current) => ({ ...current, ...changes }));
  }

  function buildCapture(
    duplicateDecision?: "create_if_no_match" | "create_anyway",
  ): QuickInteractionCaptureDto | null {
    const resolvedContact =
      contactId ||
      contacts.find(
        (contact) =>
          contact.isActive &&
          contact.name.trim().toLocaleLowerCase() ===
            personQuery.trim().toLocaleLowerCase(),
      )?.id;
    const primaryContact = resolvedContact
      ? { contactId: resolvedContact }
      : personQuery.trim()
        ? {
            name: personQuery.trim(),
            email: null,
            phone: null,
            duplicateDecision: duplicateDecision ?? "create_if_no_match",
          }
        : undefined;
    const hasMeaningfulFacts = Boolean(
      primaryContact ||
        note.trim() ||
        (activeLabId &&
          ((pipettes.enabled && categoryHasFacts(pipettes)) ||
            (balances.enabled && categoryHasFacts(balances)))) ||
        (confirmIdentity && resolvedContact) ||
        (recordResponsibility && resolvedContact),
    );
    const inferredOutcome =
      captureOutcome ?? (hasMeaningfulFacts ? "information_captured" : null);
    if (!inferredOutcome) {
      setFieldError("Add something you learned or choose a quick outcome.");
      return null;
    }
    if (stickerCapture && (!activeLabId || !categoryHasFacts(pipettes))) {
      setFieldError("Add sticker category, provider, or timing before saving.");
      return null;
    }
    let occurredAtIso: string;
    try {
      occurredAtIso = localDateTimeToISOString(occurredAt);
    } catch (caught) {
      setFieldError(
        caught instanceof Error
          ? caught.message
          : "Enter a valid date and time.",
      );
      return null;
    }
    const raw: Record<string, unknown> = {
      clientRequestId,
      context: { labId: activeLabId ?? null, locationId: location.id },
      occurredAt: occurredAtIso,
      interactionType,
      communicationDirection,
      captureOutcome: inferredOutcome,
      note,
      ...(primaryContact ? { primaryContact } : {}),
    };
    if (confirmIdentity && resolvedContact) {
      raw.contactIdentity = { verificationStatus: "verified" };
    }
    if (activeLabId && recordResponsibility && resolvedContact) {
      raw.contactResponsibility = {
        role: responsibilityRole,
        calibrationResponsible,
        influenceScope,
        relationshipStatus,
        ...(scopeNote.trim() ? { scopeNote: scopeNote.trim() } : {}),
      };
    }
    if (activeLabId && pipettes.enabled) {
      const value = buildCategory(pipettes, interactionType);
      if (value) raw.pipettes = value;
    }
    if (activeLabId && balances.enabled) {
      const value = buildCategory(balances, interactionType);
      if (value) raw.balances = value;
    }
    const parsed = quickInteractionCaptureSchema.safeParse(raw);
    if (!parsed.success) {
      setFieldError(
        parsed.error.issues[0]?.message ?? "Check the capture fields.",
      );
      return null;
    }
    return parsed.data;
  }

  async function submit(
    duplicateDecision?: "create_if_no_match" | "create_anyway",
  ): Promise<void> {
    setError(null);
    setFieldError(null);
    setSaving(true);
    const capture = buildCapture(duplicateDecision);
    if (!capture) {
      setSaving(false);
      return;
    }
    try {
      const result = await api.saveInteraction(capture);
      setDuplicateCandidates([]);
      await onSaved(result, savedSummary(capture, result));
    } catch (caught) {
      if (
        caught instanceof ApiError &&
        caught.code === "duplicate_review_required"
      ) {
        setDuplicateCandidates(caught.details?.candidates ?? []);
      } else {
        setError(
          caught instanceof Error
            ? caught.message
            : "The interaction could not be saved.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  const drawer = presentation === "drawer";
  return (
    <>
      {/*
        The backdrop is a real control rather than a div with a handler, so
        dismissing by pointer is keyboard- and screen-reader-reachable too.
      */}
      <button
        type="button"
        className="capture-backdrop"
        aria-label="Dismiss without saving"
        tabIndex={-1}
        onClick={onClose}
      />
      <div className={drawer ? "drawer-layer" : "modal-layer"}>
        <section
          className={drawer ? "capture-sheet capture-drawer" : "capture-sheet"}
          role="dialog"
          aria-modal="true"
          aria-labelledby="capture-title"
        >
          <div className="sheet-heading">
            <div>
              <p className="eyebrow">Quick interaction</p>
              <h2 id="capture-title">
                {activeLabId ? "Record interaction" : "Record at this location"}
              </h2>
              <p className="context-line">
                {activeLabId
                  ? `${activeLabName ?? "Selected lab"} · ${location.name}`
                  : `${location.name} · No lab selected`}
              </p>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close interaction form"
            >
              ×
            </button>
          </div>

          <p className="capture-question">What did you learn?</p>

          {!stickerCapture && (
            <fieldset className="quick-outcomes">
              <legend className="quick-outcomes-legend">
                Quick outcome <span className="muted">optional</span>
              </legend>
              {(
                [
                  ["nobody_available", "Nobody available"],
                  ["wrong_person", "Wrong person"],
                  ["no_new_information", "No useful information"],
                  ["referred_elsewhere", "Referred elsewhere"],
                ] as const
              ).map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={
                    captureOutcome === value
                      ? "quick-choice selected"
                      : "quick-choice"
                  }
                  aria-pressed={captureOutcome === value}
                  onClick={() => selectQuickOutcome(value)}
                >
                  {label}
                </button>
              ))}
            </fieldset>
          )}

          {lightweightCapture && (
            <section className="quick-lightweight" aria-live="polite">
              <strong>
                {captureOutcome ? quickOutcomeLabels[captureOutcome] : ""}
              </strong>
              <p>
                Save this quick note and keep moving. You can add service
                details if you learned them.
              </p>
            </section>
          )}

          {captureOutcome !== "nobody_available" && !stickerCapture && (
            <section className="quick-section">
              <label htmlFor="person-search">Person</label>
              <input
                id="person-search"
                list="contact-options"
                value={personQuery}
                onChange={(event) => updatePerson(event.target.value)}
                placeholder="Search or enter person..."
              />
              <datalist id="contact-options">
                {contacts
                  .filter((contact) => contact.isActive)
                  .map((contact) => (
                    <option key={contact.id} value={contact.name}>
                      {contactLabel(contact)}
                    </option>
                  ))}
              </datalist>
              {selectedContact && (
                <p className="inline-help">
                  {contactLabel(selectedContact)} selected.
                </p>
              )}
              {personQuery.trim() && !selectedContact && (
                <p className="inline-help">
                  New person will be checked for duplicates before saving.
                </p>
              )}
            </section>
          )}

          {(serviceDetailsOpen || stickerCapture) &&
            !activeLabId &&
            (availableLabs.length > 0 || onRequestNewLab) && (
              <section className="quick-section capture-lab-choice">
                <label htmlFor="service-lab">Lab for service information</label>
                {availableLabs.length > 0 && (
                  <select
                    id="service-lab"
                    value={selectedLabId}
                    onChange={(event) => selectLab(event.target.value)}
                  >
                    <option value="">Choose a lab</option>
                    {availableLabs.map((availableLab) => (
                      <option key={availableLab.id} value={availableLab.id}>
                        {availableLab.name}
                      </option>
                    ))}
                  </select>
                )}
                {onRequestNewLab && (
                  <button
                    type="button"
                    className="text-button"
                    onClick={onRequestNewLab}
                  >
                    + New lab
                  </button>
                )}
                <p className="inline-help">
                  General location notes do not need a lab.
                </p>
              </section>
            )}

          {stickerCapture && serviceFactsVisible && (
            <CategorySection
              title="Pipettes"
              category={pipettes}
              onChange={(changes) => updateCategory("pipettes", changes)}
              interactionType={interactionType}
              forceOpen
            />
          )}

          {serviceFactsVisible && !stickerCapture && (
            <div className="category-sections">
              <CategorySection
                title="Pipettes"
                category={pipettes}
                onChange={(changes) => updateCategory("pipettes", changes)}
                interactionType={interactionType}
              />
              {balances.enabled ? (
                <CategorySection
                  title="Balances"
                  category={balances}
                  onChange={(changes) => updateCategory("balances", changes)}
                  interactionType={interactionType}
                />
              ) : (
                <button
                  type="button"
                  className="add-category-button"
                  onClick={() => updateCategory("balances", { enabled: true })}
                >
                  + Balances
                </button>
              )}
            </div>
          )}

          {!stickerCapture && (
            <div className="capture-utility-row">
              <button
                type="button"
                className="text-button"
                onClick={() => setNoteOpen((value) => !value)}
                aria-expanded={noteOpen}
              >
                {noteOpen ? "Hide note" : "+ Note"}
              </button>
              {!serviceFactsVisible && (
                <button
                  type="button"
                  className="text-button"
                  onClick={() => setServiceDetailsOpen((value) => !value)}
                  aria-expanded={serviceDetailsOpen}
                >
                  {serviceDetailsOpen
                    ? "Hide service details"
                    : "+ Service details"}
                </button>
              )}
              <button
                type="button"
                className="text-button"
                onClick={() => chooseInteractionType("sticker_observation")}
              >
                Sticker observed
              </button>
              <button
                type="button"
                className="text-button"
                onClick={() => setDetailsOpen((value) => !value)}
                aria-expanded={detailsOpen}
              >
                {detailsOpen ? "Hide details" : "More details"}
              </button>
            </div>
          )}

          {noteOpen && (
            <label>
              Note <span className="muted">optional</span>
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={3}
                placeholder="Only add what will help later."
              />
            </label>
          )}

          {detailsOpen && (
            <section className="advanced-details">
              <div className="form-grid compact-grid">
                <label>
                  Interaction type
                  <select
                    value={interactionType}
                    onChange={(event) =>
                      chooseInteractionType(event.target.value)
                    }
                  >
                    <option value="field_visit">Field visit</option>
                    <option value="hallway">Hallway visit</option>
                    <option value="phone">Phone call</option>
                    <option value="email">Email</option>
                    <option value="referral">Referral</option>
                    <option value="sticker_observation">
                      Sticker observed
                    </option>
                    <option value="other">Other</option>
                  </select>
                </label>
                {(interactionType === "phone" ||
                  interactionType === "email") && (
                  <label>
                    Direction
                    <select
                      value={communicationDirection}
                      onChange={(event) =>
                        setCommunicationDirection(event.target.value)
                      }
                    >
                      <option value="unknown">Unknown</option>
                      <option value="outbound">Outbound</option>
                      <option value="inbound">Inbound</option>
                    </select>
                  </label>
                )}
                <label>
                  Occurred
                  <input
                    type="datetime-local"
                    value={occurredAt}
                    onChange={(event) => setOccurredAt(event.target.value)}
                  />
                </label>
              </div>
              {!stickerCapture && selectedContact && activeLabId && (
                <div className="advanced-contact-details">
                  <label className="choice">
                    <input
                      type="checkbox"
                      checked={confirmIdentity}
                      onChange={(event) =>
                        setConfirmIdentity(event.target.checked)
                      }
                    />
                    Confirm this person identity
                  </label>
                  <label className="choice">
                    <input
                      type="checkbox"
                      checked={recordResponsibility}
                      onChange={(event) =>
                        setRecordResponsibility(event.target.checked)
                      }
                    />
                    Record or update their relationship to this Lab
                  </label>
                  {recordResponsibility && (
                    <div className="form-grid compact-grid">
                      <label>
                        Role
                        <select
                          value={responsibilityRole ?? ""}
                          onChange={(event) =>
                            setResponsibilityRole(event.target.value || null)
                          }
                        >
                          <option value="">Not asserted</option>
                          <option value="lab_manager">Lab manager</option>
                          <option value="calibration_coordinator">
                            Calibration coordinator
                          </option>
                          <option value="purchasing">Purchasing</option>
                          <option value="quality">Quality</option>
                          <option value="principal_investigator">
                            Principal investigator
                          </option>
                          <option value="decision_maker">Decision-maker</option>
                          <option value="other">Other</option>
                        </select>
                      </label>
                      <label>
                        Relationship status
                        <select
                          value={relationshipStatus}
                          onChange={(event) =>
                            setRelationshipStatus(
                              event.target
                                .value as Relationship["relationshipStatus"],
                            )
                          }
                        >
                          <option value="possible">Possible</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="former">Former</option>
                        </select>
                      </label>
                      <label>
                        Influence scope
                        <select
                          value={influenceScope ?? ""}
                          onChange={(event) =>
                            setInfluenceScope(event.target.value || null)
                          }
                        >
                          <option value="">Not asserted</option>
                          <option value="lab">Lab</option>
                          <option value="department">Department</option>
                          <option value="building">Building</option>
                          <option value="campus">Campus</option>
                          <option value="institution">Institution</option>
                        </select>
                      </label>
                      <label>
                        Handles calibration for this Lab
                        <select
                          value={
                            calibrationResponsible === null
                              ? ""
                              : String(calibrationResponsible)
                          }
                          onChange={(event) =>
                            setCalibrationResponsible(
                              event.target.value === ""
                                ? null
                                : event.target.value === "true",
                            )
                          }
                        >
                          <option value="">Not asserted</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </label>
                      <label>
                        Context <span className="muted">optional</span>
                        <input
                          value={scopeNote}
                          onChange={(event) => setScopeNote(event.target.value)}
                          placeholder="Covers the third floor"
                        />
                      </label>
                    </div>
                  )}
                  {relationshipForContact && (
                    <p className="inline-help">
                      Current relationship:{" "}
                      {relationshipForContact.relationshipStatus} ·{" "}
                      {relationshipForContact.influenceScope ??
                        "scope not asserted"}
                    </p>
                  )}
                </div>
              )}
            </section>
          )}

          {(fieldError || error) && (
            <p className="error-message" role="alert">
              {fieldError ?? error}
            </p>
          )}
          {duplicateCandidates.length > 0 && (
            <div className="duplicate-review" role="alert">
              <strong>Possible matching person</strong>
              <p>
                Review the candidate before deciding whether this is a new
                person.
              </p>
              {duplicateCandidates.map((candidate) => (
                <div className="candidate" key={candidate.id}>
                  <b>{candidate.name}</b>
                  <span>
                    {[
                      candidate.title,
                      candidate.organizationName,
                      candidate.locationName,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              ))}
              <div className="button-row">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    const candidate = duplicateCandidates[0];
                    setPersonQuery(candidate?.name ?? "");
                    setContactId(candidate?.id ?? "");
                    setDuplicateCandidates([]);
                  }}
                  disabled={saving}
                >
                  Use existing
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => void submit("create_anyway")}
                  disabled={saving}
                >
                  Create different person
                </button>
              </div>
            </div>
          )}
          <div className="sheet-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={() => void submit()}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}

interface CategorySectionProps {
  title: "Pipettes" | "Balances";
  category: CategoryDraft;
  onChange: (changes: Partial<CategoryDraft>) => void;
  interactionType: string;
  forceOpen?: boolean;
}

function CategorySection({
  title,
  category,
  onChange,
  interactionType,
  forceOpen = false,
}: CategorySectionProps) {
  return (
    <fieldset className="category-card active">
      <legend>{title}</legend>
      {!category.enabled && !forceOpen ? (
        <button
          type="button"
          className="add-category-button"
          onClick={() => onChange({ enabled: true })}
        >
          + {title}
        </button>
      ) : (
        <div className="category-fields">
          <label>
            Quantity <span className="muted">approximate by default</span>
            <span className="quantity-input">
              {category.qualifier === "approximate" && (
                <span aria-hidden="true">~</span>
              )}
              <input
                aria-label={`${title} quantity`}
                inputMode="numeric"
                type="number"
                min="0"
                value={category.quantity}
                onChange={(event) => onChange({ quantity: event.target.value })}
              />
            </span>
          </label>
          <TimingEditor category={category} onChange={onChange} />
          <label>
            Provider <span className="muted">optional</span>
            <input
              value={category.provider}
              onChange={(event) => onChange({ provider: event.target.value })}
              placeholder={
                interactionType === "sticker_observation"
                  ? "CalibraMax"
                  : "Provider"
              }
            />
          </label>
          {!forceOpen && (
            <button
              type="button"
              className="text-button category-remove"
              onClick={() => onChange({ enabled: false })}
            >
              Remove {title.toLowerCase()}
            </button>
          )}
          <details className="category-advanced">
            <summary>Category details</summary>
            <div className="form-grid compact-grid">
              <label>
                Quantity type
                <select
                  value={category.quantityScope}
                  onChange={(event) =>
                    onChange({
                      quantityScope: event.target
                        .value as CategoryDraft["quantityScope"],
                    })
                  }
                >
                  <option value="current_installed_base">
                    Current equipment
                  </option>
                  <option value="historical_service">Historical service</option>
                </select>
              </label>
              <label>
                Precision
                <select
                  value={category.qualifier}
                  onChange={(event) =>
                    onChange({
                      qualifier: event.target
                        .value as CategoryDraft["qualifier"],
                    })
                  }
                >
                  <option value="approximate">Approximate</option>
                  <option value="exact">Exact</option>
                </select>
              </label>
              {category.quantityScope === "historical_service" && (
                <label>
                  Service date
                  <input
                    type="date"
                    value={category.effectiveDate}
                    onChange={(event) =>
                      onChange({ effectiveDate: event.target.value })
                    }
                  />
                </label>
              )}
              <label>
                Accreditation need
                <select
                  value={category.accreditationRequirement}
                  onChange={(event) =>
                    onChange({
                      accreditationRequirement: event.target
                        .value as CategoryDraft["accreditationRequirement"],
                    })
                  }
                >
                  <option value="unknown">Unknown</option>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
              <label>
                Cycle months <span className="muted">optional</span>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={category.cycleMonths}
                  onChange={(event) =>
                    onChange({ cycleMonths: event.target.value })
                  }
                  placeholder="12"
                />
              </label>
            </div>
          </details>
        </div>
      )}
    </fieldset>
  );
}

function TimingEditor({
  category,
  onChange,
}: {
  category: CategoryDraft;
  onChange: (changes: Partial<CategoryDraft>) => void;
}) {
  const setMonth = (month: number) =>
    onChange({ timingPrecision: "month", month: String(month) });
  const setSeason = (season: CategoryDraft["season"]) =>
    onChange({ timingPrecision: "season", season });
  return (
    <div className="timing-editor">
      <span className="timing-label">Timing</span>
      <div className="timing-quick-grid">
        <button
          type="button"
          className={category.timingPrecision === "unknown" ? "selected" : ""}
          aria-pressed={category.timingPrecision === "unknown"}
          onClick={() => onChange({ timingPrecision: "unknown" })}
        >
          Not sure
        </button>
        {monthLabels.map((month, index) => (
          <button
            type="button"
            key={month}
            className={
              category.timingPrecision === "month" &&
              category.month === String(index + 1)
                ? "selected"
                : ""
            }
            aria-pressed={
              category.timingPrecision === "month" &&
              category.month === String(index + 1)
            }
            onClick={() => setMonth(index + 1)}
          >
            {month.slice(0, 3)}
          </button>
        ))}
        {Object.entries(seasonLabels).map(([value, label]) => (
          <button
            type="button"
            key={value}
            className={
              category.timingPrecision === "season" && category.season === value
                ? "selected"
                : ""
            }
            aria-pressed={
              category.timingPrecision === "season" && category.season === value
            }
            onClick={() => setSeason(value as CategoryDraft["season"])}
          >
            {label}
          </button>
        ))}
        {(["due_soon", "overdue", "just_serviced"] as const).map((state) => (
          <button
            type="button"
            key={state}
            className={
              category.timingPrecision === "relative" &&
              category.relativeState === state
                ? "selected"
                : ""
            }
            aria-pressed={
              category.timingPrecision === "relative" &&
              category.relativeState === state
            }
            onClick={() =>
              onChange({ timingPrecision: "relative", relativeState: state })
            }
          >
            {state === "due_soon"
              ? "Due soon"
              : state === "just_serviced"
                ? "Just serviced"
                : "Overdue"}
          </button>
        ))}
      </div>
      <div className="timing-secondary-row">
        <button
          type="button"
          className={
            category.timingPrecision === "exact_date" ? "selected" : ""
          }
          onClick={() => onChange({ timingPrecision: "exact_date" })}
        >
          Exact date
        </button>
        <button
          type="button"
          className={
            category.timingPrecision === "date_range" ? "selected" : ""
          }
          onClick={() => onChange({ timingPrecision: "date_range" })}
        >
          Other / range
        </button>
      </div>
      {category.timingPrecision === "exact_date" && (
        <label>
          Exact date
          <input
            type="date"
            value={category.exactDate}
            onChange={(event) => onChange({ exactDate: event.target.value })}
          />
        </label>
      )}
      {category.timingPrecision === "date_range" && (
        <div className="form-grid compact-grid">
          <label>
            Starts
            <input
              type="date"
              value={category.rangeStart}
              onChange={(event) => onChange({ rangeStart: event.target.value })}
            />
          </label>
          <label>
            Ends
            <input
              type="date"
              value={category.rangeEnd}
              onChange={(event) => onChange({ rangeEnd: event.target.value })}
            />
          </label>
        </div>
      )}
      {category.timingPrecision === "relative" && (
        <label>
          Reference date <span className="muted">optional</span>
          <input
            type="date"
            value={category.referenceDate}
            onChange={(event) =>
              onChange({ referenceDate: event.target.value })
            }
          />
        </label>
      )}
    </div>
  );
}
